import type { GifiRow } from '@hvac/contracts';

/**
 * Money typed by a person, to cents as a string: "12,345.67", "$1,200",
 * "(3,000.00)" and "-3000" all read. Null when it is not an amount.
 */
export function dollarsToMinor(raw: string): string | null {
  let text = raw.trim().replace(/[$,\s]/g, '');
  if (text === '') return '0';
  let negative = false;
  if (/^\(.*\)$/.test(text)) {
    negative = true;
    text = text.slice(1, -1);
  }
  if (text.startsWith('-')) {
    negative = !negative;
    text = text.slice(1);
  }
  const match = /^(\d{1,13})(?:\.(\d{1,2}))?$/.exec(text);
  if (match === null) return null;
  const cents = BigInt(match[1] ?? '0') * 100n + BigInt((match[2] ?? '').padEnd(2, '0'));
  return (negative && cents !== 0n ? -cents : cents).toString();
}

/** Cents to the plain figure a person types back: "12345.67". */
export function minorToDollars(minor: string): string {
  const value = BigInt(minor);
  const sign = value < 0n ? '-' : '';
  const abs = value < 0n ? -value : value;
  return `${sign}${(abs / 100n).toString()}.${(abs % 100n).toString().padStart(2, '0')}`;
}

/**
 * Last year's Schedule 100, pasted from tax software or typed: each line a
 * four-digit GIFI code and an amount. Whatever else a line holds (the item's
 * name, a column of prior-year figures) is ignored; the first amount after
 * the code is taken. Lines without a code are skipped, and reported.
 */
export function parseGifiPaste(text: string): {
  items: { gifi: string; amountMinor: string }[];
  unread: string[];
} {
  const items: { gifi: string; amountMinor: string }[] = [];
  const unread: string[] = [];
  for (const line of text.split(/\r?\n/)) {
    const trimmed = line.trim();
    if (trimmed === '') continue;
    const code = /^(\d{4})\b(.*)$/.exec(trimmed);
    const amount = code?.[2]?.match(/\(?-?\$?\s?\d[\d,]*(?:\.\d{1,2})?\)?/);
    const minor = amount === null || amount === undefined ? null : dollarsToMinor(amount[0]);
    if (code?.[1] === undefined || minor === null) {
      unread.push(trimmed);
      continue;
    }
    items.push({ gifi: code[1], amountMinor: minor });
  }
  return { items, unread };
}

/**
 * The schedules as a file for the accountant's tax software: one line per
 * item, schedule, code and amount in dollars. Items without a code are left
 * out; the page names them, because they cannot be filed as they are.
 */
export function gifiCsv(rows: readonly GifiRow[]): string {
  const lines = rows
    .filter((row) => row.gifiCode !== null)
    .map((row) => `${row.schedule},${row.gifiCode ?? ''},${minorToDollars(row.amountMinor)}`);
  return ['Schedule,GIFI,Amount', ...lines].join('\n') + '\n';
}
